import * as React from 'react';
import { ChatEntityLinkProps, ChatEntityRegistry } from '../share/components/chat/entityRegistry';

import AddressLink from '../address/components/address/AddressDetail/AddressLink';
import UserLink from '../auth/components/user/UserDetail/UserLink';
import LocationLink from '../location/components/location/LocationDetail/LocationLink';

import AccountingUserLink from '../timber/components/accountingUser/AccountingUserDetail/AccountingUserLink';
import BuyerLink from '../timber/components/buyer/BuyerDetail/BuyerLink';
import BuyerUserLink from '../timber/components/buyerUser/BuyerUserDetail/BuyerUserLink';
import ContractorLink from '../timber/components/contractor/ContractorDetail/ContractorLink';
import ContractorUserLink from '../timber/components/contractorUser/ContractorUserDetail/ContractorUserLink';
import CordwoodLink from '../timber/components/cordwood/CordwoodDetail/CordwoodLink';
import CuttingLink from '../timber/components/cutting/CuttingDetail/CuttingLink';
import ForestOwnerLink from '../timber/components/forestOwner/ForestOwnerDetail/ForestOwnerLink';
import LogLink from '../timber/components/log/LogDetail/LogLink';
import ProviderLink from '../timber/components/provider/ProviderDetail/ProviderLink';
import ProviderUserLink from '../timber/components/providerUser/ProviderUserDetail/ProviderUserLink';
import RoadCarrierLink from '../timber/components/roadCarrier/RoadCarrierDetail/RoadCarrierLink';
import ShipmentLink from '../timber/components/shipment/ShipmentDetail/ShipmentLink';
import ThicknessClassLink from '../timber/components/thicknessClass/ThicknessClassDetail/ThicknessClassLink';
import TimberClassLink from '../timber/components/timberClass/TimberClassDetail/TimberClassLink';
import TimberStorageLink from '../timber/components/timberStorage/TimberStorageDetail/TimberStorageLink';
import TimberTypeLink from '../timber/components/timberType/TimberTypeDetail/TimberTypeLink';

/**
 * Adapts an entity component to the chat's contract.
 *
 * The application's entity components take an id and an icon, and the icon
 * is not always optional. The chat has an id and nothing else, and an icon
 * that means nothing would only take room, so the wrapper leaves it out.
 */
const entityLink = (Link: React.ComponentType<any>): React.FC<ChatEntityLinkProps> =>
  function ChatEntityLink({ id }: ChatEntityLinkProps) {
    return <Link id={id} />;
  };

/**
 * The records the chat can link to.
 *
 * The keys are the kinds the API names its fields after: a field called
 * `cuttingId` is a cutting, `storageId` is a timber storage. The components
 * are the ones the rest of the application uses to show a record, so a name
 * in the chat looks like a name everywhere else -- and the link goes to the
 * same page a list row would.
 *
 * This lives in the application layer rather than in the chat because the
 * chat is written not to know about timber: the packages own their records,
 * and the application is where the packages meet.
 */
export const chatEntityRegistry: ChatEntityRegistry = {
  address: { Link: entityLink(AddressLink) },
  user: { Link: entityLink(UserLink) },
  location: { Link: entityLink(LocationLink) },

  accountingUser: { Link: entityLink(AccountingUserLink) },
  buyer: { Link: entityLink(BuyerLink) },
  buyerUser: { Link: entityLink(BuyerUserLink) },
  contractor: { Link: entityLink(ContractorLink) },
  contractorUser: { Link: entityLink(ContractorUserLink) },
  cordwood: { Link: entityLink(CordwoodLink) },
  cutting: { Link: entityLink(CuttingLink) },
  forestOwner: { Link: entityLink(ForestOwnerLink) },
  log: { Link: entityLink(LogLink) },
  provider: { Link: entityLink(ProviderLink) },
  providerUser: { Link: entityLink(ProviderUserLink) },
  roadCarrier: { Link: entityLink(RoadCarrierLink) },
  shipment: { Link: entityLink(ShipmentLink) },
  thicknessClass: { Link: entityLink(ThicknessClassLink) },
  timberClass: { Link: entityLink(TimberClassLink) },
  timberStorage: { Link: entityLink(TimberStorageLink) },
  timberType: { Link: entityLink(TimberTypeLink) },
};
